// Standard (non-urlsafe, padded) base64 encoding/decoding.
//
// The btoa/atob fallback below is vendored verbatim from tweetnacl-util
// (public domain, by Dmitry Chestnykh and Devi Mandiri). It is slightly ugly,
// but battle-tested, faster than js implementations, and is what we de facto
// used everywhere via that dependency until now. We keep it as the universal
// baseline and layer faster paths on top:
//
//   1. Native `Uint8Array.toBase64()` / `Uint8Array.fromBase64()` — the TC39
//      standard. Available in modern browsers, and preferred whenever present.
//      This is not yet enabled by default in Node. As of Node 24 (V8 13.6)
//      these are still gated behind the experimental `--js-base-64` flag.
//   2. Node `Buffer` — the fast path that covers all our current Node
//      runtimes, since (1) is not yet available on Node by default. Do NOT
//      drop this until the native methods ship unflagged on Node; otherwise
//      Node would fall through to the slow (3) below.
//   3. btoa/atob fallback — for any environment that has neither of the above.

type Base64EncodeOptions = { alphabet?: 'base64' | 'base64url'; omitPadding?: boolean };
type Base64DecodeOptions = {
  alphabet?: 'base64' | 'base64url';
  lastChunkHandling?: 'loose' | 'strict' | 'stop-before-partial';
};
interface NativeToBase64 {
  toBase64(options?: Base64EncodeOptions): string;
}
interface NativeFromBase64 {
  fromBase64(s: string, options?: Base64DecodeOptions): Uint8Array<ArrayBuffer>;
}

const u8proto = Uint8Array.prototype as Partial<NativeToBase64>;
const u8ctor = Uint8Array as Partial<NativeFromBase64>;
const hasBuffer = typeof Buffer !== 'undefined';

// Matches canonical, padded base64. Vendored from tweetnacl-util.
const BASE64_RE =
  /^(?:[A-Za-z0-9+/]{2}[A-Za-z0-9+/]{2})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/;

function validateBase64(s: string): void {
  if (!BASE64_RE.test(s)) {
    throw new TypeError('invalid encoding');
  }
}

export function encodeBase64(data: Uint8Array): string {
  if (u8proto.toBase64) {
    return u8proto.toBase64.call(data);
  }
  // Active path on Node today; see header note before removing.
  if (hasBuffer) {
    return Buffer.from(data).toString('base64');
  }
  return encodeBase64Fallback(data);
}

// Return type is narrowed to `Uint8Array<ArrayBuffer>`: every path produces a
// fresh ArrayBuffer-backed array (never SharedArrayBuffer), so consumers don't
// need to re-narrow.
export function decodeBase64(s: string): Uint8Array<ArrayBuffer> {
  // The native path performs its own validation (throws on invalid input); the
  // fallbacks below rely on validateBase64, since Buffer silently ignores
  // invalid characters rather than throwing.
  if (u8ctor.fromBase64) {
    return u8ctor.fromBase64(s);
  }
  validateBase64(s);
  // Active path on Node today; see header note before removing.
  if (hasBuffer) {
    return new Uint8Array(Buffer.from(s, 'base64'));
  }
  return decodeBase64Fallback(s);
}

// Internal function, exported for benchmarking
export function encodeBase64Fallback(arr: Uint8Array): string {
  const chars: string[] = [];
  for (let i = 0; i < arr.length; i++) {
    chars.push(String.fromCharCode(arr[i]!));
  }
  return btoa(chars.join(''));
}

// Internal function, exported for benchmarking
export function decodeBase64Fallback(s: string): Uint8Array<ArrayBuffer> {
  const decoded = atob(s);
  const bytes = new Uint8Array(decoded.length);
  for (let i = 0; i < decoded.length; i++) {
    bytes[i] = decoded.charCodeAt(i);
  }
  return bytes;
}
